import React, { useCallback, useMemo, useState } from 'react';
import { Switch, Tag, Typography, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../../components/list';
import LeadPipelineStageViewDrawer from './LeadPipelineStageViewDrawer';
import LeadPipelineStageFormDrawer from './LeadPipelineStageFormDrawer';
import { updateLeadPipelineStage } from '../api/leadPipelineStages';
import { resolveProblemDetailsMessage } from '../../../../lib/api/problemMessage';
import type { ProblemDetails } from '../../../../lib/api/types';
import {
  closingOutcomeLabel,
  isClosedStageCategory,
  stageCategoryLabel,
} from '../stageMetadata';
import { useLeadPipelineStages } from '../hooks/useLeadPipelineStages';
import { useLeadPipelineStage } from '../hooks/useLeadPipelineStage';
import { useCreateLeadPipelineStage } from '../hooks/useCreateLeadPipelineStage';
import { useUpdateLeadPipelineStage } from '../hooks/useUpdateLeadPipelineStage';
import { useDeleteLeadPipelineStage } from '../hooks/useDeleteLeadPipelineStage';
import type { LeadPipelineStageListItem, LeadPipelineStageFormValues } from '../types';

const LeadPipelineStagesPage: React.FC = () => {
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formStageId, setFormStageId] = useState<number | null>(null);

  const { data: stages = [], isLoading: loadingList } = useLeadPipelineStages();
  const { data: stageDetail, isLoading: loadingStage } = useLeadPipelineStage(
    formMode === 'edit' ? formStageId : null,
  );
  const viewStage = useLeadPipelineStage(selectedStageId);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteLeadPipelineStage();

  const toggleMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: LeadPipelineStageFormValues }) =>
      updateLeadPipelineStage(id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings-lead-pipeline-stages'] });
    },
    onError: (error) => {
      const detail = error && typeof error === 'object' && 'response' in error
        ? resolveProblemDetailsMessage(
            (error as { response?: { data?: ProblemDetails } }).response?.data ?? {},
            'Failed to toggle stage active status',
          )
        : 'Failed to toggle stage active status';
      notification.error({ message: detail });
    },
  });

  const createMutation = useCreateLeadPipelineStage(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateLeadPipelineStage(formStageId, () => {
    setFormDrawerOpen(false);
  });

  const handleAdd = useCallback(() => {
    setFormMode('add');
    setFormStageId(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((id: number) => {
    setFormMode('edit');
    setFormStageId(id);
    setFormDrawerOpen(true);
  }, []);

  const columns: ColumnsType<LeadPipelineStageListItem> = useMemo(
    () => [
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
        render: (v: string, record: LeadPipelineStageListItem) => (
          <Typography.Text strong>
            {v}
            {record.isSystem && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                System
              </Tag>
            )}
          </Typography.Text>
        ),
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        width: 140,
      },
      {
        title: 'Sort',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.sortOrder - b.sortOrder,
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 100,
        render: (v: LeadPipelineStageListItem['category']) => (
          <Tag color={isClosedStageCategory(v) ? 'default' : 'blue'}>{stageCategoryLabel(v)}</Tag>
        ),
      },
      {
        title: 'Outcome',
        dataIndex: 'closingStatus',
        key: 'closingStatus',
        width: 120,
        render: (v: LeadPipelineStageListItem['closingStatus']) => {
          const label = closingOutcomeLabel(v);
          if (label === '—') {
            return label;
          }
          return <Tag>{label}</Tag>;
        },
      },
      {
        title: 'Active',
        key: 'status',
        width: 100,
        render: (_: unknown, record: LeadPipelineStageListItem) => (
          <Switch
            checked={record.isActive}
            disabled={record.isSystem}
            loading={
              toggleMutation.isPending && toggleMutation.variables?.id === record.id
            }
            onChange={(checked) => {
              toggleMutation.mutate({
                id: record.id,
                values: {
                  code: record.code,
                  label: record.label,
                  sortOrder: record.sortOrder,
                  category: record.category,
                  closingStatus: record.closingStatus,
                  isActive: checked,
                },
              });
            }}
          />
        ),
      },
      buildCogActionsColumn<LeadPipelineStageListItem>({
        onView: (record) => setSelectedStageId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deletePermission: 'lead-pipeline-stages.delete',
        deleteDisabled: (record) => record.isSystem,
        deleteConfirm: () => ({
          title: 'Delete pipeline stage',
          content: 'Delete this stage? Leads on it must be moved first. System stages cannot be deleted.',
        }),
        rowNumber: (_record, index) => index + 1,
      }),
    ],
    [deleteMutation, toggleMutation, handleEdit],
  );

  const handleEditFromView = () => {
    if (selectedStageId !== null) {
      handleEdit(selectedStageId);
    }
  };

  const handleSaveForm = (values: LeadPipelineStageFormValues) => {
    console.log('handleSaveForm values:', values);
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formStageId !== null) {
      updateMutation.mutate(values);
    }
  };

  return (
    <>
      <ListPage
        title="Stages"
        subtitle="Configure pipeline stages, closing categories, and display order."
        primaryAction={{
          permission: 'lead-pipeline-stages.create',
          label: 'Add stage',
          onClick: handleAdd,
        }}
      >
        <DataTable<LeadPipelineStageListItem>
          columns={columns}
          dataSource={stages}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={false}
          emptyText="No pipeline stages configured."
        />
      </ListPage>

      <LeadPipelineStageViewDrawer
        open={selectedStageId !== null}
        onClose={() => setSelectedStageId(null)}
        stage={viewStage.data}
        loading={viewStage.isLoading}
        onEdit={handleEditFromView}
      />

      <LeadPipelineStageFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        stage={stageDetail}
        loadingStage={formMode === 'edit' && formStageId !== null ? loadingStage : false}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};

export default LeadPipelineStagesPage;
